import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'qs';

import {
  Typography,
  Divider,
  Box,
  Button,
  Grid
} from '@material-ui/core';

import TopSection from '../common/topSection';
import Loading from '../common/loading';
import Alert from '../common/alert';
import ItemsList from './itemsList';
import ItemSearchFilters from './ItemSearchFilters';

import { fetchItems, selectItems } from './itemsSlice';
import { saveSearch, clearSaveStatus, selectSaveStatus } from '../itemSearches/itemSearchesSlice';
import { selectActiveSearch, setActiveSearch } from '../itemSearches/itemSearchSlice';
import { enqueueSnackbar } from '../snackbar/snackbarSlice';

import useTitle from '../../hooks/use-title.hook';

const defaultFilterState = {
  activeOperator: 'AND',
  activeFilters: [
    {
      type: '',
      operation: '',
      field: '',
      value: '',
      id: 'search-row-default'
    }
  ]
};

const ItemsPage = (props) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { items, status: itemsStatus, error: itemsError } = useSelector(selectItems);
  const { status: itemSearchStatus, error: itemSearchError } = useSelector(selectSaveStatus);
  const activeSearch = useSelector(selectActiveSearch);
  const [ filterState, setFilterState ] = useState(activeSearch?.data || defaultFilterState);

  const showResultsTitle = items.length > 0;

  const itemsWithRouterLinks = items.map(item => ({
    ...item,
    link: `/pih/owner/${item.itemOwnerId}/items/${item.itemCode}`
  }));

  const clearFilterState = () => {
    setFilterState(defaultFilterState);
  }

  const handleSaveSearch = event => {
    dispatch(saveSearch(filterState));
  };

  const handleSearch = event => {
    // Remember default empty row
    if(filterState.activeFilters.length === 1 && (!filterState.activeFilters[0].field || !filterState.activeFilters[0].operation)) {
      dispatch(fetchItems());
    } else {
      dispatch(setActiveSearch({data: filterState}));
      dispatch(fetchItems(filterState));
    }

    // Clear url
    history.replace({
      search: ''
    });
  };

  useEffect(() => {
    // Check once on load if query param should autosearch
    if(location.search) {
      const queryParams = qs.parse(location.search.replace('?', ''));

      if(queryParams.itemFamilyCode) {
        // Autopopulate familycode to filters
        const newState = {
          activeOperator: 'AND',
          activeFilters: [
            {
              type: 'multiChoice',
              operation: 'oneOf',
              field: 'itemFamilyCode',
              value: [queryParams.itemFamilyCode],
              id: 'search-row-default'
            }
          ],
        };
        setFilterState(newState);

        // Search immediately
        dispatch(fetchItems(newState));

      }
    }
  }, []);

  useEffect(() => {
    // Show save feedback
    if(itemSearchStatus === 'failed') {
      dispatch(enqueueSnackbar({
        message: t(`errors:${itemSearchError}`),
        options: {
          variant: 'error'
        }
      }));
      dispatch(clearSaveStatus());
    } else if(itemSearchStatus === 'succeeded') {
      dispatch(enqueueSnackbar({
        message: t('itemSearch:searchSavedSuccessfully'),
        options: {
          variant: 'success'
        }
      }));
      dispatch(clearSaveStatus());
    }
  }, [itemSearchStatus]);

  useTitle(t('item:items'));

  return (
    <>
      <TopSection>
        <Typography variant="h4" component="h1">{t('item:items')}</Typography>
      </TopSection>
      <ItemSearchFilters state={filterState} setState={setFilterState} clearState={clearFilterState} handleSearch={handleSearch} />
      {showResultsTitle
        ?
        <>
          <Box py={2} px={3}>
            <Grid container spacing={4}>
              <Grid item>
                <Typography variant="h6" component="h2">{t('itemSearch:results')}</Typography>
              </Grid>
              <Grid item>
                <Button variant="contained" color="primary" onClick={handleSaveSearch} disabled={itemSearchStatus === 'loading'} disableElevation={true}>{t('itemSearch:saveSearch')}</Button>
              </Grid>
            </Grid>
          </Box>
          <Divider />
        </>
        : null
      }

      <Box p={3}>
        {itemsStatus === 'succeeded' ? <ItemsList items={itemsWithRouterLinks} showNoResultsMessage /> : null}
        {itemsStatus === 'loading' ? <Loading message={t('itemSearch:loadingResults')}/> : null}
        {itemsStatus === 'failed' && itemsError ? <Alert severity="error">{t(`errors:${itemsError.message}`)}</Alert> : null}
      </Box>
    </>
  )
};

export default ItemsPage;
